import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, Info, X } from 'lucide-react';

/**
 * Themed dialog that replaces native window.confirm / window.alert
 * Props:
 *   open        - boolean
 *   title       - string
 *   message     - string
 *   type        - 'alert' | 'confirm' | 'save-discard'
 *   onConfirm   - () => void  (OK / Save)
 *   onCancel    - () => void  (Cancel / Discard / Close)
 *   confirmText - string (default: 'OK')
 *   cancelText  - string (default: 'Cancel')
 */
function ThemedDialog({ open, title, message, type = 'alert', onConfirm, onCancel, confirmText, cancelText }) {
  if (!open) return null;

  const icon = type === 'alert'
    ? <Info className="w-6 h-6 text-primary" />
    : type === 'save-discard'
      ? <AlertTriangle className="w-6 h-6 text-amber-500" />
      : <CheckCircle className="w-6 h-6 text-primary" />;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4"
        onClick={onCancel}
        data-testid="themed-dialog-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          className="bg-card-solid border border-border rounded-2xl shadow-2xl max-w-sm w-full p-6"
          onClick={(e) => e.stopPropagation()}
          data-testid="themed-dialog"
        >
          <div className="flex items-start space-x-3 mb-4">
            <div className="flex-shrink-0 mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-medium text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{message}</p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            {type === 'alert' ? (
              <button
                onClick={onCancel || onConfirm}
                className="bg-primary text-white hover:bg-primary/80 rounded-full px-5 py-2 text-sm font-medium transition-colors"
                data-testid="dialog-ok-button"
              >
                {confirmText || 'OK'}
              </button>
            ) : type === 'save-discard' ? (
              <>
                <button
                  onClick={onCancel}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-4 py-2"
                  data-testid="dialog-discard-button"
                >
                  {cancelText || 'Discard'}
                </button>
                <button
                  onClick={onConfirm}
                  className="bg-primary text-white hover:bg-primary/80 rounded-full px-5 py-2 text-sm font-medium transition-colors"
                  data-testid="dialog-save-button"
                >
                  {confirmText || 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onCancel}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-4 py-2"
                  data-testid="dialog-cancel-button"
                >
                  {cancelText || 'Cancel'}
                </button>
                <button
                  onClick={onConfirm}
                  className="bg-primary text-white hover:bg-primary/80 rounded-full px-5 py-2 text-sm font-medium transition-colors"
                  data-testid="dialog-confirm-button"
                >
                  {confirmText || 'OK'}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ThemedDialog;
